import { FC } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';
import { WWWCompanyDocument } from '../../../common/documents/WWWCompanyDocument';
import { Header } from '../Header';
import { Help } from '../Help';
import { useCheckSelectedID } from '../../context/advancedSearchStore';
import { useScopedState } from '../../lib/state';
import { staticState } from '../../context/staticStore';
import { mediaQuery } from '../Theme';
import { Grid, Column, Item, Spacer } from './CompanyGrid';
import { Field, Chip } from './CompanyFieldAndHelpers';
import { CompanyEditDialogContent } from './CompanyEditDialog';
import { CompanyEditSelect } from './CompanyEditSelect';
import { useCompanyDictionary } from './CompanyDictionary';
import { CompanyMap } from './CompanyMap';

const CompanyMapColumn = styled.div`
  display: flex;

  > div:first-of-type {
    flex-shrink: 0;
  }

  > div:last-of-type {
    flex: 1;
    margin-left: 1rem;
  }

  ${mediaQuery.small(css`
    margin-top: 1rem;
    flex-direction: column;
    align-items: center;

    > div:last-of-type {
      margin-top: 1rem;
      margin-left: 0;
    }
  `)}
`;

export type CompanyOperationInfoProps = Pick<
  WWWCompanyDocument,
  'tol' | 'contractingForms' | 'talo80' | 'operationalAreas' | 'projectTypes' | 'machineryTypes' | 'customerTargetGroups'
>;

interface Props {
  company: WWWCompanyDocument;
  editable: boolean;
  premium: boolean;
  loading: boolean;
}

export const CompanyOperationInfo: FC<Props> = ({ company, editable, premium, loading }) => {
  const dictionary = useCompanyDictionary();

  const isTalo80IDSelected = useCheckSelectedID('talo80');
  const isTolIDSelected = useCheckSelectedID('tol2008');
  const isContractingFormsIDSelected = useCheckSelectedID('contractingForms');
  const isProjectTypesIDSelected = useCheckSelectedID('projectTypes');
  const isOperationalAreasIDSelected = useCheckSelectedID('operationalAreas');
  const isMachineryTypesIDSelected = useCheckSelectedID('machineryTypes');
  const isTargetGroupsIDSelected = useCheckSelectedID('targetGroups');

  const { tol, contractingForms, talo80, operationalAreas, projectTypes, machineryTypes, customerTargetGroups } = company;

  const commonFieldProps = {
    premium,
    loading,
  };

  const isPremiumLoading = premium && loading;

  return (
    <Grid>
      <Column>
        <Item>
          <Field label={dictionary.talo80} {...commonFieldProps} chip empty={!talo80.length}>
            {talo80.map((p, i) => (
              <Chip key={i} selected={isTalo80IDSelected(p.talo80ID)}>
                {p.name}
              </Chip>
            ))}
          </Field>
        </Item>
        <Item>
          <Field label={dictionary.tol} {...commonFieldProps} chip empty={!tol?.name}>
            <Chip selected={isTolIDSelected(tol?.tol2008ID || 0)}>{tol?.name || ''}</Chip>
          </Field>
        </Item>

        <Spacer />

        <Item>
          <Field label={dictionary.contractingForms} {...commonFieldProps} chip empty={!contractingForms.length}>
            {contractingForms.map((p, i) => (
              <Chip key={i} selected={isContractingFormsIDSelected(p.contractingFormID)}>
                {p.name}
              </Chip>
            ))}
          </Field>
        </Item>
        <Item>
          <Field label={dictionary.projectTypes} {...commonFieldProps} chip empty={!projectTypes.length}>
            {projectTypes.map((p, i) => (
              <Chip key={i} selected={isProjectTypesIDSelected(p.projectTypeID)}>
                {p.name}
              </Chip>
            ))}
          </Field>
        </Item>
      </Column>
      <Column>
        <Item>
          <CompanyMapColumn>
            <CompanyMap operationalAreas={operationalAreas} />

            <Field label={dictionary.operationalAreas} {...commonFieldProps} chip empty={!operationalAreas.length}>
              {operationalAreas.map((p, i) => (
                <Chip key={i} selected={isOperationalAreasIDSelected(p.areaID)}>
                  {p.name}
                </Chip>
              ))}
            </Field>
          </CompanyMapColumn>
        </Item>

        <Spacer show={isPremiumLoading || editable || !premium || machineryTypes.length} />

        <Item show={isPremiumLoading || editable || !premium || machineryTypes.length}>
          <Field label={dictionary.machineryTypes} {...commonFieldProps} chip empty={!machineryTypes.length}>
            {machineryTypes.map((p, i) => (
              <Chip key={i} selected={isMachineryTypesIDSelected(p.machineryTypeID)}>
                {p.name}
              </Chip>
            ))}
          </Field>
        </Item>

        <Spacer show={isPremiumLoading || editable || !premium || customerTargetGroups.length} />

        <Item show={isPremiumLoading || editable || !premium || customerTargetGroups.length}>
          <Field label={dictionary.customerTargetGroups} {...commonFieldProps} chip empty={!customerTargetGroups.length}>
            {customerTargetGroups.map((p, i) => (
              <Chip key={i} selected={isTargetGroupsIDSelected(p.targetGroupID)}>
                {p.name}
              </Chip>
            ))}
          </Field>
        </Item>
      </Column>
    </Grid>
  );
};

export const CompanyOperationInfoEdit: CompanyEditDialogContent = ({ company, openSelect, closeSelect }) => {
  const intl = useIntl();

  const title = intl.formatMessage({ id: 'company.operationsTitle', defaultMessage: 'Toiminta' });

  const dictionary = useCompanyDictionary();
  const { talo80, contractingForms, projectTypes, operationalAreas, machineryTypes, targetGroups } = useScopedState(staticState, [
    'talo80',
    'contractingForms',
    'projectTypes',
    'operationalAreas',
    'machineryTypes',
    'targetGroups',
  ]);

  return (
    <>
      <Header>{title}</Header>

      <CompanyEditSelect
        parentTitle={title}
        title={dictionary.talo80}
        path="talo80"
        items={talo80}
        onOpen={openSelect}
        onClose={closeSelect}
      />
      <Help>
        <FormattedMessage id="company.talo80.helpText" defaultMessage="Yrityksesi toimialaluokat Talo 80 nimikkeistöjärjestelmästä" />
      </Help>

      <CompanyEditSelect
        parentTitle={title}
        title={dictionary.contractingForms}
        path="contractingForms"
        items={contractingForms}
        onOpen={openSelect}
        onClose={closeSelect}
      />

      <CompanyEditSelect
        parentTitle={title}
        title={dictionary.projectTypes}
        path="projectTypes"
        items={projectTypes}
        onOpen={openSelect}
        onClose={closeSelect}
      />

      <CompanyEditSelect
        parentTitle={title}
        title={dictionary.operationalAreas}
        path="operationalAreas"
        items={operationalAreas}
        onOpen={openSelect}
        onClose={closeSelect}
      />
      <Help>
        <FormattedMessage id="company.operationalAreas.helpText" defaultMessage="Alueet joilla yrityksesi on mahdolista toimia" />
      </Help>

      <CompanyEditSelect
        parentTitle={title}
        title={dictionary.machineryTypes}
        path="machineryTypes"
        items={machineryTypes}
        onOpen={openSelect}
        onClose={closeSelect}
      />

      <CompanyEditSelect
        parentTitle={title}
        title={dictionary.customerTargetGroups}
        path="customerTargetGroups"
        items={targetGroups}
        onOpen={openSelect}
        onClose={closeSelect}
      />
    </>
  );
};
